import './App.css';
import React from 'react';
import Header from './components/Header/Header';
import { ConfigProvider } from 'antd';
import ImageViewerModal from './components/Common/ImageViewer';
import FloatingButton from './components/Common/FloatButton';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PrivacyPolicy from './components/PrivacyPolicy/PrivacyPolicy';
import CookiePolicy from './components/CookiePolicy/CookiePolicy';
import GeoRedirect from './components/GeoRedirect';
import HomeRegional from './components/HomeRegional';
import TermsOfServices from './components/TermsOfServices/TermsOfServices';
import Redirector from './components/Common/Redirector';
import Demo from './components/Demo/Demo';
import CookieConsent from './components/CookieConsent/CookieConsent';
import AboutSection from './components/AboutSection/AboutSection';
import AboutPage from './components/About/AboutPage';
import WhyShipmnts from './components/WhyShipmnts/WhyShipmnts';
import PricingComponent from './components/PricingPage/PricingComponent';
import RegionalProductRedirect from './components/Common/RegionalProductRedirect';

// Product pages
import SalesPage from './components/Products/SalesPage';
import ForwardingPage from './components/Products/ForwardingPage';
import ForwardingPageUAE from './components/Products/ForwardingPageUAE';
import ForwardingPageUS from './components/Products/ForwardingPageUS';
import CustomsPage from './components/Products/CustomsPage';
import CustomsPageUAE from './components/Products/CustomsPageUAE';
import CustomsPageUS from './components/Products/CustomsPageUS';
import TransportPage from './components/Products/TransportPage';
import WarehousePage from './components/Products/WarehousePage';
import FinancePage from './components/Products/FinancePage';
import FinancePageUAE from './components/Products/FinancePageUAE';
import FinancePageUS from './components/Products/FinancePageUS';
import AnalyticsPage from './components/Products/AnalyticsPage';
import AdminPage from './components/Products/AdminPage';


function CanonicalTag() {
  const { pathname } = useLocation();
  return (
    <Helmet>
      <link rel="canonical" href={`https://shipmnts.com${pathname}`} />
    </Helmet>
  );
}

// Syncs the detected region into sessionStorage whenever the app is on a
// regional path. This ensures detectRegion() returns instantly (from cache)
// on subsequent client-side navigations, even when the initial landing was a
// server-side CloudFront redirect that bypassed the GeoRedirect component.
function RegionSync() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    const match = pathname.match(/^\/(in|uae|us)(\/|$)/);
    if (match) {
      sessionStorage.setItem('shipmnts_region', match[1]);
    }
  }, [pathname]);
  return null;
}

function App() {
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#fe6533',
          controlHeightLG: '60px',
        },
      }}
    >
      <>
        <ImageViewerModal />
        <FloatingButton shape="square" />
        <CookieConsent />

        <RegionSync />
        <CanonicalTag />
        <Header />
        <Routes>
          {/* ── Global root (geo-detects and redirects) ── */}
          <Route path="/" element={<GeoRedirect />} />

          {/* ── Regional homepages ── */}
          <Route path="in" element={<HomeRegional region="in" />} />
          <Route path="uae" element={<HomeRegional region="uae" />} />
          <Route path="us" element={<HomeRegional region="us" />} />

          {/* ── Global / shared pages ── */}
          <Route path="demo" element={<Demo />} />
          <Route path="privacy-policy" element={<PrivacyPolicy />} />
          <Route path="cookie-policy" element={<CookiePolicy />} />
          <Route path="terms-of-services" element={<TermsOfServices />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="why-shipmnts" element={<WhyShipmnts />} />

          {/* ── Product pages: global (geo-redirect wrappers) ── */}
          <Route path="products/sales" element={<SalesPage />} />
          <Route
            path="products/forwarding"
            element={
              <RegionalProductRedirect
                paths={{ in: '/in/products/forwarding', uae: '/uae/products/forwarding', us: '/us/products/forwarding' }}
                FallbackComponent={ForwardingPage}
              />
            }
          />
          <Route
            path="products/customs"
            element={
              <RegionalProductRedirect
                paths={{ in: '/in/products/customs' }}
                FallbackComponent={CustomsPage}
              />
            }
          />
          <Route
            path="products/finance"
            element={
              <RegionalProductRedirect
                paths={{ in: '/in/products/finance', uae: '/uae/products/finance', us: '/us/products/finance' }}
                FallbackComponent={FinancePage}
              />
            }
          />
          <Route path="products/transport" element={<TransportPage />} />
          <Route path="products/warehouse" element={<WarehousePage />} />
          <Route path="products/analytics" element={<AnalyticsPage />} />
          <Route path="products/admin" element={<AdminPage />} />

          {/* ── Product pages: India ── */}
          <Route path="in/products/forwarding" element={<ForwardingPage />} />
          <Route path="in/products/customs" element={<CustomsPage />} />
          <Route path="in/products/finance" element={<FinancePage />} />

          {/* ── Product pages: UAE ── */}
          <Route path="uae/products/forwarding" element={<ForwardingPageUAE />} />
          <Route path="uae/products/finance" element={<FinancePageUAE />} />
          <Route path="uae/products/customs" element={<CustomsPageUAE />} />

          {/* ── Product pages: US ── */}
          <Route path="us/products/forwarding" element={<ForwardingPageUS />} />
          <Route path="us/products/finance" element={<FinancePageUS />} />
          <Route path="us/products/customs" element={<CustomsPageUS />} />

          <Route
            path="blog/*"
            element={<Redirector pattern="^/blog(.*)$" target="https://blog.shipmnts.com$1" />}
          />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        <PricingComponent />
        <AboutSection />
      </>
    </ConfigProvider>
  );
}

export default App;
